import { Menu } from "@ark-ui/react";
import { isUserKey } from "@packages/core";
import {
  Link,
  type LinkProps,
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { type ReactElement, cloneElement, memo, useMemo } from "react";
import {
  LuChevronsUpDown,
  LuCircleUserRound,
  LuFileText,
  LuHeartPulse,
  LuHospital,
  LuLayoutGrid,
  LuLogOut,
  LuMessageSquareWarning,
  LuNetwork,
  LuPocketKnife,
  LuReceiptJapaneseYen,
  LuSend,
  LuTriangleAlert,
  LuUsersRound,
} from "react-icons/lu";
import { Reha30LogoA, TypographyStyle, menuStyles } from "#components";
import {
  getNativeApplicationEdition,
  useApplicationConfigurationQuery,
  useAuthenticationStore,
  useCurrentUsername,
  useNativeApplicationRevisionQuery,
  useNativeApplicationVersionQuery,
  useOperator,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management")({ component: Component });

const items = [
  {
    available: true,
    icon: <LuLayoutGrid />,
    label: "%Dashboard.0%",
    path: "/management/system-dashboard",
  },
  {
    available: true,
    icon: <LuHospital />,
    label: "%CustomerManagement.0%",
    path: "/management/organizations",
  },
  {
    available: true,
    icon: <LuNetwork />,
    label: "%ProviderManagement.0%",
    path: "/management/providers",
  },
  {
    available: false,
    icon: <LuTriangleAlert />,
    label: "%SensorManagement.0%",
    path: "/dummy",
  },
  {
    available: true,
    icon: <LuHeartPulse />,
    label: "%ResidentDataConfirmation.0%",
    path: "/management/resident-data-exporter",
  },
  {
    available: false,
    icon: <LuSend />,
    label: "%NotificationBroadcast.0%",
    path: "/dummy",
  },
  {
    available: false,
    icon: <LuReceiptJapaneseYen />,
    label: "%ChargeManagement.0%",
    path: "/dummy",
  },
  {
    available: false,
    icon: <LuUsersRound />,
    label: "%OperatorManagement.0%",
    path: "/dummy",
  },
  {
    available: false,
    icon: <LuFileText />,
    label: "%TermsOfServiceEdit.0%",
    path: "/dummy",
  },
  {
    available: true,
    icon: <LuPocketKnife />,
    label: "%Utility.0%",
    path: "/management/utilities",
  },
  {
    available: true,
    icon: <LuMessageSquareWarning />,
    label: "%ErrorLogNotification.0%",
    path: "/management/watchtowers",
  },
] as const;

function Component() {
  const translate = useTranslate();
  const location = useLocation();
  const logOut = useAuthenticationStore(({ logOut }) => logOut);
  const { data: applicationConfiguration } = useApplicationConfigurationQuery();
  const nativeApplicationRevisionQuery = useNativeApplicationRevisionQuery();
  const nativeApplicationVersionQuery = useNativeApplicationVersionQuery();
  const navigate = useNavigate();
  const username = useCurrentUsername();

  const $operator = useOperator(isUserKey(username) && username);

  /** ネイティブアプリケーションの更新が必要な場合、`true` */
  const outdated = useMemo(
    function () {
      if (!applicationConfiguration || !nativeApplicationRevisionQuery.data) {
        return false;
      }

      const requiredApplicationConfiguration =
        getNativeApplicationEdition() === "Android"
          ? applicationConfiguration?.android
          : getNativeApplicationEdition() === "Ios"
            ? applicationConfiguration?.ios
            : undefined;
      const requiredRevision = requiredApplicationConfiguration?.requiredRevision ?? 0;
      const outdated = nativeApplicationRevisionQuery.data < requiredRevision;
      return outdated;
    },
    [applicationConfiguration, nativeApplicationRevisionQuery.data],
  );

  return (
    <div
      className={css({
        display: "grid",
        gridTemplateColumns: "204px 1fr",
        height: "100dvh",
        minWidth: "960px",

        "& > main, & > [data-main]": {
          height: "100%",
          overflowY: "auto",
          paddingBottom: {
            smDown: "max(16px, env(safe-area-inset-bottom))",
            sm: "max(32px, env(safe-area-inset-bottom))",
          },
          paddingLeft: { smDown: "16px", sm: "24px" },
          paddingRight: {
            smDown: "max(16px, env(safe-area-inset-right))",
            sm: "max(24px, env(safe-area-inset-right))",
          },
          paddingTop: {
            smDown: "max(16px, env(safe-area-inset-top))",
            sm: "max(32px, env(safe-area-inset-top))",
          },
        },
      })}
    >
      <div
        className={css({
          backgroundColor: "sidebar-background",
          display: "flex",
          flexDirection: "column",
          overflow: "clip auto",
          paddingBottom: "env(safe-area-inset-bottom)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingTop: "env(safe-area-inset-top)",
        })}
      >
        {outdated && (
          <div
            className={css({ alignItems: "center", display: "flex", gap: "8px", padding: "16px" })}
          >
            <p className={TypographyStyle({ fontSize: "12px", fontWeight: 700, lineHeight: 1 })}>
              {translate("%SoftwareUpdateNotice.0%")}
            </p>
          </div>
        )}

        <div
          className={css({ alignItems: "center", display: "flex", gap: "8px", padding: "16px" })}
        >
          <Reha30LogoA className={css({ height: "21px", width: "auto" })} />
          <h1 className={TypographyStyle({ fontSize: "12px", fontWeight: 700, lineHeight: 1 })}>
            {translate("%ManagementConsole.0%")}
          </h1>
        </div>

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            gap: "4px",
            margin: "8px",
          })}
        >
          {items.map(function ({ available, icon, label, path }, index) {
            return available ? (
              <MenuButton
                className={css({})}
                icon={icon}
                key={index}
                label={translate(label)}
                selected={location.pathname.startsWith(path)}
                to={path}
              />
            ) : null;
          })}
        </div>

        <Menu.Root positioning={{ placement: "top" }}>
          <Menu.Trigger asChild className={menuStyles().trigger}>
            <div
              className={css({
                alignItems: "center",
                display: "flex",
                borderRadius: "6px",
                gap: "8px",
                margin: "8px",
                padding: "8px",
                transition: "all 0.2s",
                "&:hover": { backgroundColor: "#00000008" },
              })}
            >
              <div
                className={css({ backgroundColor: "#18181B", borderRadius: "8px", padding: "8px" })}
              >
                <LuCircleUserRound
                  className={css({ color: "#FAFAFA", height: "16px", width: "auto" })}
                />
              </div>

              <div className={css({ flexGrow: 1 })}>
                <p
                  className={TypographyStyle({
                    ellipsis: true,
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: 1,
                  })}
                >
                  {$operator?.name}
                </p>
                <p
                  className={TypographyStyle({
                    ellipsis: true,
                    fontSize: "12px",
                    lineHeight: 1,
                    marginTop: "2px",
                    monospace: true,
                  })}
                >
                  {$operator?.userKey}
                </p>
              </div>

              <LuChevronsUpDown
                className={css({ display: "block", height: "16px", width: "auto" })}
              />
            </div>
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content className={menuStyles().content}>
              <Menu.Item
                className={menuStyles().item}
                onClick={function () {
                  logOut().then(async function () {
                    await navigate({ to: "/login" });
                  });
                }}
                value="1"
              >
                <LuLogOut className={css({ height: "16px", width: "auto" })} />
                <span className={TypographyStyle({ fontSize: "14px", lineHeight: 1 })}>
                  {translate("%Logout.0%")}
                </span>
              </Menu.Item>
              <div
                className={css({
                  borderColor: "border",
                  borderStyle: "solid",
                  borderTopWidth: "1px",
                })}
              />
              <Menu.Item
                className={css(
                  TypographyStyle.raw({ color: "primary/60", fontSize: "12px", lineHeight: 1 }),
                  { padding: "4px 8px" },
                )}
                value="2"
              >
                <span>{translate("%Version.0%")}</span>
                &nbsp;
                <span className={TypographyStyle({ monospace: true })}>{NPM_PACKAGE_VERSION}</span>
              </Menu.Item>
              {nativeApplicationVersionQuery.data && (
                <Menu.Item
                  className={css(
                    TypographyStyle.raw({ color: "primary/60", fontSize: "12px", lineHeight: 1 }),
                    { padding: "4px 8px" },
                  )}
                  value="3"
                >
                  <span>{translate("%Version.0%")}</span>
                  &nbsp;
                  <span className={TypographyStyle({ monospace: true })}>
                    {nativeApplicationVersionQuery.data} (
                    {nativeApplicationRevisionQuery.data ?? "?"})
                  </span>
                </Menu.Item>
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      </div>
      <Outlet />
    </div>
  );
}

type MenuButtonProperties = {
  className?: string;
  icon: ReactElement<{ className?: string }>;
  label: string;
  selected?: boolean;
} & LinkProps;
const MenuButton = memo<MenuButtonProperties>(
  function MenuButton({ className, icon, label, selected, ...properties }) {
    return (
      <Link
        {...properties}
        className={cx(
          css({
            alignItems: "center",
            borderRadius: "6px",
            cursor: "pointer",
            display: "flex",
            gap: "8px",
            paddingX: "8px",
            paddingY: "9px",
            _hover: {
              backgroundColor: "sidebar-accent",
            },
            _focus: {
              backgroundColor: "sidebar-accent",
            },
            _selected: {
              backgroundColor: "sidebar-accent",
            },
          }),
          className,
        )}
        data-selected={selected ? true : undefined}
      >
        {cloneElement(icon, { className: css({ height: "16px", width: "16px" }) })}
        <p className={css({ fontSize: "14px", fontWeight: "500" })}>{label}</p>
      </Link>
    );
  },
  function (previous, next) {
    return previous.selected === next.selected;
  },
);
